import { AvatarWithInitials } from 'components/AvatarWithInitials';
import usePastTime from 'hooks/usePastTime';
import { User } from 'models/User';
import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { getSellingPortfolio, getUserFullName } from 'utils';
import PendingActionPill from '../../PendingActionPill';
import { PENDING_ACTION_VARIANT } from '../../PendingActionPill/PendingActionPill.component';
import ChatIcon from 'icons/Chat.icon';
import DocumentWithPencilIcon from 'icons/DocumentWithPencil.icon';
import QuestionMarkCircleIcon from 'icons/QuestionMarkCircle.icon';
import { useTranslation } from 'react-i18next';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import NotesIcon from 'icons/Notes.icon';
import FolderIcon from 'icons/Folder.icon';
import DottedSpeechBubbleIcon from 'icons/DottedSpeechBubble.icon';

export type SuggestedActionsTableData = {
  _id: string;

  dealId: string;
  dealName: string;
  dataRoomId: string;
  owner: User;

  pendingActions: {
    messages: number;
    leadNotes: number;
    dealNotes: number;
    questions: number;
    waitingRoom: number;

    hasDataRoomFiles: boolean;
    hasFaqQuestion: boolean;
  };

  lastUpdated: Date; // Last activity
};

export const COLUMNS: Array<keyof SuggestedActionsTableData> = [
  'dealName',
  'owner',
  'pendingActions',
  'lastUpdated',
];

export const HEADING_TRANSLATIONS: Partial<
  Record<typeof COLUMNS[number], string>
> = {
  dealName: 'deal',
  owner: 'owner',
  pendingActions: 'pendingActions',
  lastUpdated: 'lastActivity',
};

export const SORTING_VALUES: Partial<Record<typeof COLUMNS[number], string>> = {
  dealName: 'dealName',
  owner: 'ownerName',
  lastUpdated: 'lastActivity',
};

export const MULTI_SORT_VALUES: Array<{ value: string; label: string }> = [
  { value: 'dealName', label: 'deal' },
  { value: 'ownerName', label: 'owner' },
  { value: 'lastActivity', label: 'lastActivity' },
];

// Define custom column sizes (optional)
export const CUSTOM_COL_SIZES: Partial<Record<typeof COLUMNS[number], number>> =
  {
    dealName: 200,
    owner: 200,
    pendingActions: 300,
    lastUpdated: 70,
  };

type CellProps = {
  data: SuggestedActionsTableData;
};

const DealNameCell: React.FC<CellProps> = ({ data }) => {
  const { dealId, dealName } = data;

  return (
    <Link
      className="as-link-black"
      to={getSellingPortfolio(dealId)}
      onClick={(e) => e.stopPropagation()}
    >
      {dealName}
    </Link>
  );
};

export const OwnerCell: React.FC<CellProps> = ({ data }) => {
  const { owner } = data;

  const { firstName, lastName } = owner || {};

  return (
    <div className="owner-cell">
      <AvatarWithInitials user={owner} />
      <p>{getUserFullName(firstName, lastName)}</p>
    </div>
  );
};

const getVariant = (count: number) => {
  if (count < 3) return PENDING_ACTION_VARIANT.DEFAULT;

  if (count < 7) return PENDING_ACTION_VARIANT.WARNING;

  return PENDING_ACTION_VARIANT.ERROR;
};

export const PendingActionsCell: React.FC<CellProps> = ({ data }) => {
  const { pendingActions } = data;

  const {
    messages,
    dealNotes,
    leadNotes,
    questions,
    waitingRoom,
    hasDataRoomFiles,
    hasFaqQuestion,
  } = pendingActions || {};

  const { t } = useTranslation();

  const hasPendingActions =
    !!messages ||
    !!dealNotes ||
    !!leadNotes ||
    !!questions ||
    !!waitingRoom ||
    !hasDataRoomFiles ||
    !hasFaqQuestion;

  if (!hasPendingActions)
    return (
      <div className="pending-actions-cell">
        {!hasPendingActions && <p>{t('noPendingActions')}</p>}
      </div>
    );
  return (
    <div className="pending-actions-cell">
      {!!messages && (
        <PendingActionPill
          variant={getVariant(messages)}
          text={messages.toString()}
          tooltipContent={t('messagesWaiting', { count: messages })}
        >
          <ChatIcon className="bigger" />
        </PendingActionPill>
      )}

      {!!dealNotes && (
        <PendingActionPill
          variant={getVariant(dealNotes)}
          text={dealNotes.toString()}
          tooltipContent={t('dealNotesUnread', { count: dealNotes })}
        >
          <NotesIcon className="padding" />
        </PendingActionPill>
      )}

      {!!leadNotes && (
        <PendingActionPill
          variant={getVariant(leadNotes)}
          text={leadNotes.toString()}
          tooltipContent={t('leadNotesUnread', { count: leadNotes })}
        >
          <DocumentWithPencilIcon className="bigger" />
        </PendingActionPill>
      )}

      {!!questions && (
        <PendingActionPill
          variant={getVariant(questions)}
          text={questions.toString()}
          tooltipContent={t('questionsWaiting', { count: questions })}
        >
          <QuestionMarkCircleIcon />
        </PendingActionPill>
      )}

      {!!waitingRoom && (
        <PendingActionPill
          variant={getVariant(waitingRoom)}
          text={waitingRoom.toString()}
          tooltipContent={t('waitingInTheWaitingRoom', { count: waitingRoom })}
        >
          <GroupUsersIcon />
        </PendingActionPill>
      )}

      {!hasDataRoomFiles && (
        <PendingActionPill
          text={t('empty')}
          tooltipContent={t('dataRoomIsEmpty')}
        >
          <FolderIcon />
        </PendingActionPill>
      )}

      {!hasFaqQuestion && (
        <PendingActionPill text={t('none')} tooltipContent={t('noFAQadded')}>
          <DottedSpeechBubbleIcon />
        </PendingActionPill>
      )}
    </div>
  );
};

export const LastActivityCell: React.FC<CellProps> = ({ data }) => {
  const { lastUpdated } = data;

  const pastTime = usePastTime()(lastUpdated);

  return <div>{pastTime}</div>;
};

export const BODY_CONTENT_GENERATE: Record<
  keyof SuggestedActionsTableData,
  (cellProps: CellProps) => ReactNode
> = {
  _id: null,
  dealId: null,
  dataRoomId: null,
  dealName: (cellProps: CellProps) => <DealNameCell {...cellProps} />,
  owner: (cellProps: CellProps) => <OwnerCell {...cellProps} />,
  pendingActions: (cellProps: CellProps) => (
    <PendingActionsCell {...cellProps} />
  ),
  lastUpdated: (cellProps: CellProps) => <LastActivityCell {...cellProps} />,
};
